/**
 * WordPress dependencies
 */
import { Button, Dashicon, TextControl } from '@safe-wordpress/components';
import { useDispatch, useSelect } from '@safe-wordpress/data';
import { _x } from '@safe-wordpress/i18n';
import { MediaUpload } from '@safe-wordpress/media-utils';

/**
 * External dependencies
 */
import { store as NAB_DATA } from '@nab/data';
import type { CssWorkingContentValue, Maybe } from '@nab/types';

type ImageEditorProps = {
	readonly selector: string;
};

export const ImageEditor = ( {
	selector,
}: ImageEditorProps ): JSX.Element | null => {
	const value = useValue( selector );
	const setAttributes = useSetAttributes( value );
	if ( ! value ) {
		return null;
	}

	return (
		<>
			{ 'active' !== value.status && (
				<div className="nab-content-panel__content__missing-element-warning">
					<Dashicon icon="warning" />
					<span>
						{ _x(
							'This element was not found on the current page',
							'text',
							'nelio-ab-testing'
						) }
					</span>
				</div>
			) }
			<TextControl
				label={ _x( 'Image URL', 'text', 'nelio-ab-testing' ) }
				value={ value.src }
				onChange={ ( src ) => setAttributes( { src } ) }
				placeholder={ value.originalSrc }
			/>
			<TextControl
				label={ _x( 'Alt Text', 'text', 'nelio-ab-testing' ) }
				value={ value.alt }
				onChange={ ( alt ) => setAttributes( { alt } ) }
				placeholder={ value.originalAlt }
			/>
			<MediaUpload
				title={ _x( 'Media Library', 'text', 'nelio-ab-testing' ) }
				allowedTypes={ [ 'image' ] }
				onSelect={ ( image ) =>
					setAttributes( {
						alt: image.alt,
						src: image.url,
					} )
				}
				render={ ( { open } ) => (
					<div className="nab-content-panel__content__media-library-button">
						<Button variant="secondary" onClick={ open }>
							{ _x(
								'Media Library',
								'command',
								'nelio-ab-testing'
							) }
						</Button>
					</div>
				) }
			/>
		</>
	);
};

// =====
// HOOKS
// =====

type ImageValue = Extract< CssWorkingContentValue, { type: 'image' } >;
const useValue = ( selector: string ) => {
	const [ values ] = useContentValues();
	return values.find(
		( v ): v is ImageValue => 'image' === v.type && v.selector === selector
	);
};

const useSetAttributes = ( value: Maybe< ImageValue > ) => {
	const [ _, setContentValue ] = useContentValues();
	return (
		attrs: Partial< { readonly src: string; readonly alt: string } >
	) => value && setContentValue( value.selector, attrs );
};

const useContentValues = (): [
	ReadonlyArray< CssWorkingContentValue >,
	(
		selector: string,
		attrs: Partial< { readonly src: string; readonly alt: string } >
	) => void,
] => {
	const editor = useSelect(
		( select ) =>
			select( NAB_DATA ).getPageAttribute( 'css-editor/cssEditorState' ),
		[]
	);
	const { setPageAttribute } = useDispatch( NAB_DATA );

	if ( editor?.mode !== 'content-editor' ) {
		return [ [], () => void null ];
	}

	const contentValues = editor.contentValues;
	return [
		contentValues,
		( selector, attrs ) => {
			void setPageAttribute( 'css-editor/cssEditorState', {
				...editor,
				contentValues: contentValues.map( ( cv ) =>
					'image' === cv.type && cv.selector === selector
						? {
								...cv,
								wasJustCreated: undefined,
								src:
									undefined === attrs.src
										? cv.src
										: attrs.src,
								alt:
									undefined === attrs.alt
										? cv.alt
										: attrs.alt,
						  }
						: cv
				),
			} );
		},
	];
};
